import React from 'react';
import { PlusIcon } from '@heroicons/react/24/outline';
import { OverlayScrollArea } from '../components/OverlayScrollArea';
import { MiniCalendar } from './MiniCalendar';
import { APPOINTMENT_TABS } from './types';
import type { AppointmentTab, CalendarMonth } from './types';

export interface CalendarSidebarProps {
    month: CalendarMonth;
    onMonthChange: (month: CalendarMonth) => void;
    counts: Record<string, number>;
    selectedDay: string | null;
    onSelectDay: (day: string | null) => void;
    todayIso: string;
    tab: AppointmentTab;
    onTabChange: (tab: AppointmentTab) => void;
    needsNoteCount: number;
    disabled: boolean;
    readOnly: boolean;
    onAddAppointment?: () => void;
    /** Host content rendered below the tab chips (e.g. extra filters). */
    extra?: React.ReactNode;
    minMonth?: CalendarMonth;
    maxMonth?: CalendarMonth;
}

export const CalendarSidebar: React.FC<CalendarSidebarProps> = ({
    month,
    onMonthChange,
    counts,
    selectedDay,
    onSelectDay,
    todayIso,
    tab,
    onTabChange,
    needsNoteCount,
    disabled,
    readOnly,
    onAddAppointment,
    extra,
    minMonth,
    maxMonth
}) => (
    <div className="w-[390px] shrink-0 border-r border-gray-200 flex flex-col min-h-0">
        <OverlayScrollArea className="flex-1 min-h-0">
            <div className="px-5 pt-5">
                <MiniCalendar
                    month={month}
                    onMonthChange={onMonthChange}
                    counts={counts}
                    selected={selectedDay}
                    onSelect={onSelectDay}
                    todayIso={todayIso}
                    disabled={disabled}
                    minMonth={minMonth}
                    maxMonth={maxMonth}
                />
            </div>
            <div className="px-5 mt-5 pb-5 flex flex-wrap gap-1.5">
                {APPOINTMENT_TABS.map(([key, label]) => (
                    <button
                        type="button"
                        key={key}
                        disabled={disabled}
                        onClick={() => onTabChange(key)}
                        className={`whitespace-nowrap rounded-lg px-2.5 py-1.5 text-[12.5px] font-medium border transition-colors disabled:opacity-40 ${
                            tab === key
                                ? 'bg-gray-900 text-white border-gray-900'
                                : 'border-gray-200 text-gray-600 hover:border-gray-400'
                        }`}
                    >
                        {label}
                        {key === 'needsNote' && needsNoteCount ? ` ${needsNoteCount}` : ''}
                    </button>
                ))}
            </div>
            {extra && <div className="px-5 pb-5">{extra}</div>}
        </OverlayScrollArea>
        {!readOnly && (
            <div className="shrink-0 border-t border-gray-200 p-4">
                <button
                    type="button"
                    onClick={onAddAppointment}
                    disabled={disabled}
                    className="w-full flex items-center justify-center gap-2 rounded-lg bg-gray-900 px-4 py-2.5 text-[13px] font-medium text-white hover:bg-gray-800 disabled:opacity-30"
                >
                    <PlusIcon className="h-4 w-4" /> Add appointment
                </button>
            </div>
        )}
    </div>
);

export default CalendarSidebar;
